import {
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import { HelpPopover } from '@/design';

import type {
  BrokerCheckSummary,
  IAdminParticipantDetail,
  IBrokerCheckEmployment,
  IBrokerCheckFirmSummary,
  IBrokerCheckPersonSummary,
  NameMatch
} from '../../api/participant-contracts';
import { absent } from '../../components/account-facts';
import { Fact } from '../../components/admin-page';
import { formatDay } from '../../components/days';
import { PayloadTree } from '../../components/payload-tree';
import { TableFrame } from '../../components/table-frame';

/**
 * A BrokerCheck or IAPD answer as survey-api read it (R9) — the person hub design §5's `summary`,
 * the projection `BrokerCheckFactProjections` makes of the registry's record on every read. Since
 * SR-396 an `iapd.person` answer is read by the same projection as `brokercheck.person` (IAPD's
 * envelope is FINRA's, the record under `iacontent`), so it is laid out by the same parts — **each
 * naming the registry it came from** (`registryOf`), "IAPD’s name" beside "Ours", never FINRA's.
 * This file lays that document out and reads nothing else: not the payload, not the envelope, not
 * the record inside it. The alternative, a reader of FINRA's JSON on this side, is what the owner's
 * ruling 4 declined — it would be a second transcription of a schema survey-api already owns,
 * and would disagree with it the first day FINRA moved a key.
 *
 * **An absent member is not drawn**, and a part with nothing under it says "Not answered":
 * survey-api leaves out a scalar or an object FINRA did not send, and a zero or a blank in its
 * place would be an answer FINRA never gave. **A list is always sent**, `[]` where FINRA sent
 * none, so an empty one says "None". Dates are ISO days, shown as the day they name
 * (`formatDay`); FINRA's `Y` / `N` flags are shown as it sent them.
 *
 * **`name_match` is survey-api's, and it is a reading, not a verification.** It compares
 * the registry's name with the participant's profile — never with the display name, which may be
 * a login — so `unknown` is said as "no name to compare", never as a difference, and a match leads
 * to "Verify number" rather than standing for it. "Ours" beside the registry's is what survey-api
 * compared: the profile's first and last name for a person, the display name and the profile's
 * firm for a firm.
 */

type SubHeading = 'h4' | 'h5';

export function BrokerCheckReading({
  reading,
  member,
  Heading
}: {
  reading: BrokerCheckSummary;
  member: IAdminParticipantDetail;
  Heading: SubHeading;
}) {
  return (
    <div role="group" aria-label="The answer as the system read it" className="grid gap-5">
      {reading.factKey === 'brokercheck.firm' ? (
        <FirmReading summary={reading.summary} member={member} Heading={Heading} />
      ) : (
        <PersonReading
          summary={reading.summary}
          member={member}
          registry={registryOf(reading.factKey)}
          Heading={Heading}
        />
      )}
    </div>
  );
}

/** Whose record a person's reading is — the name its parts give the registry. A firm's reading is
 *  FINRA's alone: IAPD is asked about people, and firms are the ADV register's. */
function registryOf(factKey: 'brokercheck.person' | 'iapd.person'): string {
  return factKey === 'iapd.person' ? 'IAPD' : 'FINRA';
}

function PersonReading({
  summary,
  member,
  registry,
  Heading
}: {
  summary: IBrokerCheckPersonSummary;
  member: IAdminParticipantDetail;
  /** "FINRA" or "IAPD" — whose name, id and lists these are. */
  registry: string;
  Heading: SubHeading;
}) {
  const theirName = joined([summary.first_name, summary.middle_name, summary.last_name], ' ');
  const ourName = joined([member.profile.firstName, member.profile.lastName], ' ');
  const counts = summary.registration_count;
  const exams = summary.exams;
  const details = summary.broker_details;
  return (
    <>
      <Part title="Name" Heading={Heading}>
        <FactGrid>
          <Fact label={`${registry}’s name`} value={theirName ?? absent('Not answered')} />
          <Fact
            label="Ours"
            value={ourName ?? absent('No questionnaire has named this participant yet.')}
          />
          <Fact label="Other names" value={nameList(summary.other_names)} />
          <Fact label="Names" value={<NameMatchChip match={summary.name_match} />} />
        </FactGrid>
      </Part>
      <Part title="Registration" Heading={Heading}>
        {facts([
          [`${registry}’s id`, summary.individual_id],
          ['Brokerage scope', summary.bc_scope],
          ['Adviser scope', summary.ia_scope],
          ['Days in industry', summary.days_in_industry],
          ['Approved SRO registrations', counts?.sro],
          ['Approved FINRA registrations', counts?.finra],
          ['Approved state registrations', counts?.state]
        ])}
      </Part>
      <Part title="Current employment" Heading={Heading}>
        <Employments jobs={summary.current_employments} registry={registry} current />
      </Part>
      <Part title="Previous employment" Heading={Heading}>
        <Employments
          jobs={summary.previous_employments}
          registry={registry}
          current={false}
        />
      </Part>
      <Part title="Registered states" Heading={Heading}>
        {summary.registered_states.length === 0 ? (
          absent('None')
        ) : (
          <TableFrame>
            <Table aria-label="Registered states">
              <TableHeader>
                <TableRow>
                  <TableHead>State</TableHead>
                  <TableHead>Scope</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Since</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {summary.registered_states.map((state, index) => (
                  <TableRow key={index}>
                    <TableCell>{cell(state.state)}</TableCell>
                    <TableCell>{cell(state.scope)}</TableCell>
                    <TableCell>{cell(state.status)}</TableCell>
                    <TableCell>{cell(day(state.since))}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableFrame>
        )}
      </Part>
      <Part title="Disclosures" Heading={Heading}>
        {facts([
          ['Brokerage disclosures', summary.disclosure_flag],
          ['Adviser disclosures', summary.ia_disclosure_flag],
          ['Disclosures listed', summary.disclosure_count]
        ])}
      </Part>
      <Part title="Exams" Heading={Heading}>
        <div className="grid gap-3">
          {facts([
            ['State exams', exams?.state],
            ['Principal exams', exams?.principal],
            ['Product exams', exams?.product]
          ])}
          {summary.exam_categories.length === 0 ? (
            <p>{absent('No exam listed.')}</p>
          ) : (
            <TableFrame>
              <Table aria-label="Exams">
                <TableHeader>
                  <TableRow>
                    <TableHead>Exam</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Taken</TableHead>
                    <TableHead>Scope</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {summary.exam_categories.map((exam, index) => (
                    <TableRow key={index}>
                      <TableCell className="whitespace-nowrap">{cell(exam.category)}</TableCell>
                      <TableCell>{cell(exam.name)}</TableCell>
                      <TableCell className="whitespace-nowrap">{cell(day(exam.taken))}</TableCell>
                      <TableCell>{cell(exam.scope)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableFrame>
          )}
        </div>
      </Part>
      <Part title="Broker details" Heading={Heading}>
        {facts([
          ['Brokerage comments', details?.has_bc_comments],
          ['Adviser comments', details?.has_ia_comments],
          ['Report', details?.report_status]
        ])}
      </Part>
      {/* The registry's own elements, which survey-api copies whole rather than projects — so
          they are drawn whole too, not picked apart here. */}
      <Part title="Registered SROs" Heading={Heading}>
        {summary.registered_sros.length === 0 ? (
          absent('None')
        ) : (
          <PayloadTree
            value={summary.registered_sros}
            label={`Registered SROs as ${registry} sent them`}
          />
        )}
      </Part>
    </>
  );
}

function FirmReading({
  summary,
  member,
  Heading
}: {
  summary: IBrokerCheckFirmSummary;
  member: IAdminParticipantDetail;
  Heading: SubHeading;
}) {
  const office = summary.office_address;
  const officeLines = office
    ? [
        office.street1,
        office.street2,
        joined([joined([office.city, office.state], ', '), office.postal_code], ' '),
        office.country
      ].filter((line): line is string => line !== undefined && line !== null)
    : [];
  return (
    <>
      <Part title="Name" Heading={Heading}>
        <FactGrid>
          <Fact label="FINRA’s name" value={summary.firm_name ?? absent('Not answered')} />
          <Fact
            label="Ours"
            value={
              <>
                <span className="block">{member.displayName}</span>
                {member.profile.firmName !== null && (
                  <span className="block text-muted-foreground">
                    Profile’s firm: {member.profile.firmName}
                  </span>
                )}
              </>
            }
          />
          <Fact label="Other names" value={nameList(summary.other_names)} />
          <Fact label="Names" value={<NameMatchChip match={summary.name_match} />} />
        </FactGrid>
      </Part>
      <Part title="Registration" Heading={Heading}>
        {facts([
          ['FINRA’s firm id', summary.firm_id],
          ['Brokerage scope', summary.bc_scope],
          ['Adviser scope', summary.ia_scope],
          ['Adviser firm', summary.is_ia_firm],
          ['Type', summary.firm_type],
          ['Status', summary.firm_status],
          ['Adviser SEC number', mono(summary.ia_sec_number)],
          ['Brokerage SEC number', mono(summary.bd_sec_number)],
          ['Report', summary.report_status]
        ])}
      </Part>
      <Part title="Form CRS" Heading={Heading}>
        {facts([
          ['Type', summary.crs?.type],
          ['File', mono(summary.crs?.file_id)]
        ])}
      </Part>
      <Part title="Office" Heading={Heading}>
        {officeLines.length > 0 ? (
          <address className="not-italic">
            {officeLines.map((line, index) => (
              <span key={index} className="block">
                {line}
              </span>
            ))}
          </address>
        ) : null}
      </Part>
      <Part title="Disclosures" Heading={Heading}>
        {facts([
          ['Brokerage disclosures', summary.bd_disclosure_flag],
          ['Adviser disclosures', summary.ia_disclosure_flag]
        ])}
      </Part>
    </>
  );
}

/** A person's jobs — survey-api's brokerage and adviser lists already merged, and a previous
 *  one's already newest end first; drawn in the order sent. */
function Employments({
  jobs,
  registry,
  current
}: {
  jobs: IBrokerCheckEmployment[];
  registry: string;
  current: boolean;
}) {
  if (jobs.length === 0) return absent('None');
  const label = current ? 'Current employment' : 'Previous employment';
  return (
    <TableFrame>
      <Table aria-label={label}>
        <TableHeader>
          <TableRow>
            <TableHead>Firm</TableHead>
            <TableHead>SEC number</TableHead>
            <TableHead>Place</TableHead>
            <TableHead>From</TableHead>
            {!current && <TableHead>To</TableHead>}
            <TableHead>Scope</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {jobs.map((job, index) => (
            <TableRow key={index}>
              <TableCell>
                {cell(job.firm_name)}
                {job.firm_id !== undefined && (
                  <span className="block text-xs text-muted-foreground">
                    {registry}’s firm id {job.firm_id}
                  </span>
                )}
                {job.ia_only !== undefined && (
                  <span className="block text-xs text-muted-foreground">
                    Adviser only: {job.ia_only}
                  </span>
                )}
              </TableCell>
              <TableCell className="font-mono whitespace-nowrap">
                {cell(joined([job.bd_sec_number, job.ia_sec_number], ' · '))}
              </TableCell>
              <TableCell>{cell(joined([job.city, job.state], ', '))}</TableCell>
              <TableCell className="whitespace-nowrap">
                {cell(day(job.registration_begin))}
              </TableCell>
              {!current && (
                <TableCell className="whitespace-nowrap">
                  {cell(day(job.registration_end))}
                </TableCell>
              )}
              <TableCell className="whitespace-nowrap">
                {cell(
                  joined(
                    [
                      job.bc_scope && `Brokerage ${job.bc_scope}`,
                      job.ia_scope && `Adviser ${job.ia_scope}`
                    ],
                    ' · '
                  )
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableFrame>
  );
}

type ChipVariant = 'success' | 'destructive' | 'outline';

const NAME_MATCH: Record<NameMatch, { variant: ChipVariant; text: string }> = {
  match: { variant: 'success', text: 'Names match' },
  differs: { variant: 'destructive', text: 'Names differ' },
  unknown: { variant: 'outline', text: 'No name to compare' }
};

/** survey-api's comparison, said — and what it is not. */
function NameMatchChip({ match }: { match: NameMatch }) {
  // Closed in the type, open on the wire: a value survey-api adds later is shown as sent.
  const said = NAME_MATCH[match] ?? { variant: 'outline' as const, text: match as string };
  return (
    <span className="inline-flex items-center gap-1">
      <Badge variant={said.variant}>{said.text}</Badge>
      <HelpPopover label="About the name comparison">
        <p>
          {match === 'unknown'
            ? 'One side has no name to compare — a login standing in for a name is never compared.'
            : 'A comparison, not a verification — that is “Verify number”, on the number.'}
        </p>
      </HelpPopover>
    </span>
  );
}

/** One part of the reading under its own heading — "Not answered" where nothing is under it. A
 *  group named by its title, so each part is one thing to a screen reader and to a spec. */
function Part({
  title,
  Heading,
  children
}: {
  title: string;
  Heading: SubHeading;
  children: React.ReactNode;
}) {
  return (
    <div role="group" aria-label={title} className="grid gap-2">
      <Heading className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
        {title}
      </Heading>
      <div className="min-w-0">{children ?? absent('Not answered')}</div>
    </div>
  );
}

function FactGrid({ children }: { children: React.ReactNode }) {
  return <div className="grid gap-3 sm:grid-cols-2">{children}</div>;
}

/** The members survey-api sent, as facts; null when it sent none of them — which the part
 *  says as "Not answered". */
function facts(entries: [label: string, value: React.ReactNode | undefined][]): React.ReactNode {
  const sent = entries.filter(([, value]) => value !== undefined && value !== null);
  if (sent.length === 0) return null;
  return (
    <FactGrid>
      {sent.map(([label, value]) => (
        <Fact key={label} label={label} value={value} />
      ))}
    </FactGrid>
  );
}

function nameList(names: string[]): React.ReactNode {
  if (names.length === 0) return absent('None');
  return (
    <ul className="grid gap-0.5">
      {names.map((name) => (
        <li key={name}>{name}</li>
      ))}
    </ul>
  );
}

/** The parts that were sent, joined; undefined when none was. */
function joined(parts: (string | null | undefined)[], separator: string): string | undefined {
  const sent = parts.filter((part): part is string => typeof part === 'string' && part !== '');
  return sent.length === 0 ? undefined : sent.join(separator);
}

function day(value: string | undefined): string | undefined {
  return value === undefined ? undefined : formatDay(value);
}

function mono(value: string | undefined): React.ReactNode | undefined {
  return value === undefined ? undefined : <span className="font-mono">{value}</span>;
}

/** A table cell's value, or a dash that says it is not answered. */
function cell(value: string | undefined): React.ReactNode {
  if (value !== undefined) return value;
  return (
    <span className="text-muted-foreground" title="Not answered">
      —<span className="sr-only">Not answered</span>
    </span>
  );
}
